<template>
	<div class="content-wrapper">
		<!-- Content -->

		<div class="container flex-grow-1 container-p-y" v-if="training">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">Traçabilité de la formation</h4>
					<p class="mb-0">Vous pouvez retrouver ici toute la traçabilité de cette formation</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-4">
					<button class="btn btn-primary waves-effect waves-light" type="button" disabled="" v-if="isDownloading">
						<span class="spinner-border me-1" role="status" aria-hidden="true"></span>
						Chargement...
					</button>
					<button type="submit" class="btn btn-primary" @click="downloadEvents()" v-if="!isDownloading">
						Télécharger les événements
					</button>
				</div>
			</div>

			<div class="col-md-12 mb-5">
				<div class="card h-100">
					<div class="card-body pb-4">
						<div class="mb-0">
							<div class="d-flex align-items-center">
								<div class="avatar avatar-md">
									<div class="avatar-initial bg-label-primary rounded-3">
										<i class="ri-time-line ri-24px"></i>
									</div>
								</div>
								<div class="ms-4">
									<p class="mb-0">
										Le stagiaire {{ training.firstName }} a passé un total de
										<strong>{{totalTimeSpent}}</strong> sur cette formation
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="card mb-6">
				<h5 class="card-header">Suivi de la formation de {{ training.firstName }}</h5>
				<div class="card-body pt-0">
					<ul class="timeline mb-0 mt-2">
						<li class="timeline-item timeline-item-transparent" v-for="event in events" :key="event._id">
							<span :class="['timeline-point', getTimelinePointClass(event.type)]"></span>
							<div class="timeline-event">
								<div class="timeline-header mb-3">
									<h6 class="mb-0">{{ training.firstName }} {{ getEventTitle(event) }}</h6>
									<small class="text-muted">{{ formatDate(event.createdAt) }}</small>
								</div>
								<p class="mb-2">
									{{ event.customerUserAgent.browser }} • 
									{{ event.customerUserAgent.platform }} • 
									{{ event.customerIp }}
								</p>
							</div>
						</li>
						<li class="timeline-item timeline-item-transparent" v-if="training.startedAt">
							<span class="timeline-point timeline-point-success"></span>
							<div class="timeline-event">
								<div class="timeline-header mb-3">
									<h6 class="mb-0">
										{{ training.firstName }} a commencé la formation {{ training.formationName }}
									</h6>
									<small class="text-muted">{{ formatDate(training.startedAt) }}</small>
								</div>
								<p class="mb-2">Le stagiaire a cliqué sur le lien qu'il a reçu.</p>
							</div>
						</li>
						<li class="timeline-item timeline-item-transparent">
							<span class="timeline-point timeline-point-success"></span>
							<div class="timeline-event">
								<div class="timeline-header mb-3">
									<h6 class="mb-0">Inscription a la formation {{ training.formationName }}</h6>
									<small class="text-muted">{{ formatDate(training.createdAt) }}</small>
								</div>
								<p class="mb-2">Un email d'invitation a été envoyé sur {{ training.email }}</p>
							</div>
						</li>
					</ul>
				</div>
			</div>
			<div
				class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6">
				<div class="d-flex align-content-center flex-wrap gap-4">
					<router-link to="/trainings/list">
						<button class="btn btn-outline-secondary">Retour</button>
					</router-link>
				</div>
			</div>
		</div>
		<!-- / Content -->
		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import moment from "moment";
import "moment/locale/fr";

moment.locale("fr");

export default {
	name: "TrainingTimeline",

	data() {
		return {
			traineeId: null,
			isDownloading: false,
			totalTimeSpent: null,
			training: null,
			events: [],
		};
	},

	methods: {
		getTimelinePointClass(eventType) {
			const classes = {
				"viewed-video": "timeline-point-primary",
				"replied-to-question": "timeline-point-warning",
				"next-chapter": "timeline-point-success",
				"failed-quiz": "timeline-point-danger",
				"validated-quiz": "timeline-point-success",
				default: "timeline-point-secondary"
			};
			return classes[eventType] || classes.default;
		},

		getEventTitle(event) {
			const titles = {
				"viewed-video": `a visionné une video`,
				"replied-to-question": "a repondu à une question",
				"next-chapter": "a terminé un chapitre",
				"failed-quiz": `a échoué au quiz final (Score: ${event.metadata.finalScore}/100)`,
				"validated-quiz": `a validé le quiz final (Score: ${event.metadata.finalScore}/100)`,
				default: "Événement inconnu"
			};
			return titles[event.type] || titles.default;
		},

		async fetchTrainingEvents(traineeId) {
			try {
				// Appel API avec les filtres et pagination
				const response = await HTTP.get(`/trainings/${traineeId}/timeline`);

				// Mise à jour des données
				this.totalTimeSpent = response.data.totalTimeSpent;
				this.training = response.data.training;
				this.events = response.data.events;
			} catch (error) {
				// Gestion des erreurs
				const errorMessage = error?.response?.data?.message || "Une erreur est survenue, merci de contacter contact@pika-aero.com";
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async downloadEvents() {
			try {
				this.isDownloading = true;

				// Appel API pour récupérer le PDF en base64
				const response = await HTTP.post(`/trainings/${this.traineeId}/download-timeline`, { responseType: 'blob' });

				if (response.data) {
					// response.data est déjà un Blob CSV
					const blob = new Blob([response.data], { type: 'text/csv' });
					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = `training_${this.traineeId}_events.csv`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);

					this.isDownloading = false;
				} else {
					throw new Error("Fichier non disponible.");
				}
			} catch (error) {
				// Gestion des erreurs
				const errorMessage = error?.response?.data?.message || 
					"Une erreur est survenue, merci de contacter contact@pika-aero.com";
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		formatRelativeDate(date) {
			return moment(date).fromNow(); // Affiche "il y a 3 semaines"
		},

		formatDate(date) {
			return moment(date).format("llll"); // Affiche "il y a 3 semaines"
		},
	},

	async mounted() {
		this.traineeId = this.$route.params.traineeId;
		await this.fetchTrainingEvents(this.traineeId);	
	}
};
</script>

<style scoped>
/* Ajoutez des styles spécifiques à la page Dashboard */
.dashboard {
	padding: 1rem;
}

.card-title {
	font-size: 1.2rem;
}

.card-text {
	margin: 0;
}
</style>
